import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { FilterValue } from 'antd/es/table/interface'
import { InputRef, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { getColumnSearchProps } from '../../../../libs/tableFunctions'
import { CButton, CCardBody, CFormSelect } from '@coreui/react-pro'
import { useCashBoosterList } from './hooks'
import { CashBooster } from './types'
import { useStore } from 'effector-react'
import {
  $cashBoosterMonth,
  approveMonthMounted,
  changePartnerStatusMounted,
} from './model'
import { countries } from '../../../../libs/countries'
import { BlockingModal } from '../widgets/blockingModal/component'
import CIcon from '@coreui/icons-react'
import { cilCheck, cilX } from '@coreui/icons'
import { Guard } from '../../../../features/session/guard'

type Props = {
  year: number
  month: number
}

export const CashboosterList: React.FC<Props> = ({ year, month }) => {
  const {
    cashBooster,
    loading,
    totalCountOfCashBooster,
    onChangeCashBoosterList,
  } = useCashBoosterList()

  const [filters, setFilters] = useState({
    year: String(year),
    month: String(month),
  })

  const cashBoosterMonth = useStore($cashBoosterMonth)

  const isApproveButtonDisabled =
    cashBoosterMonth?.find(
      (item) =>
        item.year === Number(filters.year) &&
        item.month === Number(filters.month),
    )?.status !== 'unapproved'

  const [modalData, setModalData] = useState<{
    isOpen: boolean
    item: CashBooster | null
  }>({
    isOpen: false,
    item: null,
  })

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({
    year: String(year),
    month: String(month),
  })
  const [orderBy, setOrderBy] = useState('')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  const handleOpenBlockingModal = (item: CashBooster) => {
    setModalData((prev) => ({
      ...prev,
      isOpen: true,
      item,
    }))
  }

  const handleCloseBlockingModal = () => {
    setModalData((prev) => ({
      ...prev,
      isOpen: false,
      item: null,
    }))
  }

  const handleChangePartnerStatus = (id: number, status: string) => {
    if (window.confirm('Are you sure?')) {
      changePartnerStatusMounted({
        id,
        status,
      })
    }
  }

  useEffect(() => {
    onChangeCashBoosterList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const columns: ColumnsType<CashBooster> = [
    {
      title: 'ID',
      dataIndex: 'clientId',
      defaultSortOrder: 'descend',
      ...getColumnSearchProps<CashBooster>(
        'clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Name',
      dataIndex: 'fullName',
      ...getColumnSearchProps<CashBooster>(
        'tsname',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      filters: countries,
      filterSearch: true,
    },
    {
      title: 'Turnover in USD',
      dataIndex: 'turnover',
      sorter: true,
    },
    {
      title: 'Lots traded',
      dataIndex: 'lots',
      sorter: true,
    },
    {
      title: 'Clients',
      dataIndex: 'clients',
      sorter: true,
    },
    {
      title: 'Reward',
      key: 'reward',
      sorter: true,
      filters: [
        { text: '0', value: '0' },
        { text: '500', value: '500' },
        { text: '1000', value: '1000' },
        { text: '3000', value: '3000' },
        { text: 'rejected', value: 'rejected' },
      ],
      render: (record: CashBooster) => (
        <div className="d-flex gap-2">
          <div>
            {record.status === 'rejected' ? 'rejected ' : record.reward}
          </div>
          {record.resultId !== 0 && record.reward > 0 && (
            <div className="d-flex align-items-center">
              {['new', 'rejected'].includes(record.status) && (
                <Guard permission="restore_reward_cashbooster">
                  <CButton
                    color="success"
                    size="sm"
                    className="me-2"
                    onClick={() =>
                      handleChangePartnerStatus(record.resultId, 'approved')
                    }
                  >
                    <CIcon icon={cilCheck} />
                  </CButton>
                </Guard>
              )}
              {['new', 'approved'].includes(record.status) && (
                <Guard permission="reject_reward_cashbooster">
                  <CButton
                    color="danger"
                    size="sm"
                    onClick={() =>
                      handleChangePartnerStatus(record.resultId, 'rejected')
                    }
                  >
                    <CIcon icon={cilX} />
                  </CButton>
                </Guard>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Claimed',
      dataIndex: 'status',
      filters: [
        { text: 'YES', value: true },
        { text: 'NO', value: false },
      ],
      filterMultiple: false,
      render: (item) => <div>{item === 'claimed' ? 'YES' : 'NO'}</div>,
    },
    {
      title: 'Blocking',
      key: 'isBlocked',
      filters: [
        { text: 'YES', value: true },
        { text: 'NO', value: false },
      ],
      filterMultiple: false,
      render: (record: CashBooster) => {
        const { isBlocked } = record

        if (isBlocked) {
          const blockedLabel = record.blockedToDate
            ? `Blocked till ${record.blockedToDate.split('T')[0]}`
            : 'Blocked indefinitely'

          return (
            <Guard permission="unblock_partner_cashbooster">
              <div
                className="text-primary text-decoration-underline"
                style={{ cursor: 'pointer' }}
                onClick={() => handleOpenBlockingModal(record)}
              >
                {blockedLabel}
              </div>
            </Guard>
          )
        } else {
          return (
            <Guard permission="block_partner_cashbooster">
              <CButton
                color="danger"
                onClick={() => handleOpenBlockingModal(record)}
              >
                Block
              </CButton>
            </Guard>
          )
        }
      },
    },
  ]

  const monthOptions = cashBoosterMonth
    ?.map((item) => ({
      value: item.month,
      label: item.month,
    }))
    .filter(
      (item, index, arr) =>
        arr.findIndex((obj) => obj.value === item.value) === index,
    )

  const yearOptions = cashBoosterMonth
    ?.map((item) => ({
      value: item.year,
      label: item.year,
    }))
    .filter(
      (item, index, arr) =>
        arr.findIndex((obj) => obj.value === item.value) === index,
    )

  const handleChangeFilters = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const value = event.currentTarget.value
    const targetName = event.currentTarget.name

    setFilters((prevState) => ({
      ...prevState,
      [targetName]: value,
    }))
  }

  const handleSearch = () => {
    setSearchText((prevState) => ({
      ...prevState,
      year: filters.year,
      month: filters.month,
    }))
  }

  const handleApproveStatus = () => {
    approveMonthMounted({
      year: Number(filters.year),
      month: Number(filters.month),
      status: 'approved',
    })
  }

  return (
    <CCardBody>
      <div className="d-flex justify-content-between">
        <div className="d-flex gap-3 mb-3">
          <div>
            <CFormSelect
              value={filters.year}
              name="year"
              label="Year"
              onChange={handleChangeFilters}
            >
              {yearOptions?.map((language) => (
                <option key={language.value} value={language.value}>
                  {language.label}
                </option>
              ))}
            </CFormSelect>
          </div>
          <div>
            <CFormSelect
              name="month"
              value={filters.month}
              label="Month"
              onChange={handleChangeFilters}
            >
              {monthOptions?.map((language) => (
                <option key={language.value} value={language.value}>
                  {language.label}
                </option>
              ))}
            </CFormSelect>
          </div>
          <div className="align-content-end" onClick={handleSearch}>
            <CButton disabled={!filters.year || !filters.month}>Search</CButton>
          </div>
        </div>
        <div className="align-content-center">
          <Guard permission="approve_month_results_cashbooster">
            <CButton
              disabled={isApproveButtonDisabled}
              onClick={handleApproveStatus}
            >
              Approve Month Results
            </CButton>
          </Guard>
        </div>
      </div>
      <Table
        dataSource={cashBooster}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="cashBooster"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfCashBooster,
        }}
        onChange={(pagination, filters, sorter) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      />
      {modalData.isOpen && modalData.item && (
        <BlockingModal
          isVisible={modalData.isOpen}
          data={modalData.item}
          onClose={handleCloseBlockingModal}
        />
      )}
    </CCardBody>
  )
}
